import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import { app } from '../firebaseConfig';
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

// Remote Config defaults (used offline or before first fetch)
const DEFAULTS = {
    show_merchandise_tab: true,
    show_tour_stats: true,
    maintenance_mode: false,
    maintenance_message: '',
    welcome_banner: '',
    min_app_version: '1.0.0',
};

let remoteConfig;
try {
    remoteConfig = getRemoteConfig(app);
    remoteConfig.settings.minimumFetchIntervalMillis = 300000; // 5 min cache
    remoteConfig.defaultConfig = DEFAULTS;
} catch (e) {
    console.warn('[RemoteConfig] Failed to initialize:', e);
}

/**
 * Hook to read Firebase Remote Config values.
 * 
 * Configure values from Firebase Console:
 * https://console.firebase.google.com/project/tourminator-app/config
 * 
 * Usage:
 *   const { config, loaded } = useRemoteConfig();
 *   if (config.maintenance_mode) { show maintenance screen }
 *   if (config.show_merchandise_tab) { show tab }
 */
export const useRemoteConfig = () => {
    const [config, setConfig] = useState(DEFAULTS);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!remoteConfig) {
            setLoaded(true);
            return;
        }

        fetchAndActivate(remoteConfig)
            .then(() => {
                const values = {};
                for (const key of Object.keys(DEFAULTS)) {
                    const val = getValue(remoteConfig, key).asString();
                    try {
                        values[key] = JSON.parse(val);
                    } catch {
                        values[key] = val;
                    }
                }
                setConfig(values);
                setLoaded(true);
                console.log('[RemoteConfig] Loaded:', values);
            })
            .catch((err) => {
                console.warn('[RemoteConfig] Fetch failed, using defaults:', err);
                setLoaded(true);
            });
    }, []);

    return { config, loaded };
};

export default useRemoteConfig;
